/**
 * Google Ads click identifiers, read off the shop's `_gcl_*` cookies at handoff.
 *
 * Runs on the merchant's own domain, inside the customer's browser, next to `./ga-cookies.ts`.
 * Nothing here may import from `@/` — the bundle is self-contained (`docs/STOREFRONT.md`).
 *
 * ## Why
 *
 * A Google Ads click lands on the shop with a click id in the URL: `gclid` on most browsers
 * ("a URL parameter passed with ad clicks … required for Google Ads website conversion
 * tracking"), `wbraid` for web conversions on iOS 14+ and `gbraid` for app-to-web on iOS 14.5+
 * (https://support.google.com/google-ads/answer/10417364), `dclid` for a Display & Video 360 /
 * Campaign Manager click. Google's tag then keeps that id in **first-party cookies on the
 * shop's top domain**, prefix `_gcl`, for 90 days (Conversion Linker,
 * https://support.google.com/tagmanager/answer/7549390; the cookie list at
 * https://business.safety.google/adscookies/ names `_gcl_aw`, `_gcl_gb`, `_gcl_gs`, `_gcl_dc`
 * as "Set from partner domain", 90 days). The hosted checkout runs on another origin and can
 * never see those cookies, so — exactly as for the GA4 ids — the only place a purchase can be
 * joined back to the click is this script, at the moment the customer leaves the shop. The ids
 * ride inside the create payload, are stored on the `Checkout` row (`adClickIds`) and are
 * written onto the Shopify order as the note attributes `gclid`, `wbraid`, `gbraid`, `dclid`,
 * which is where order-based trackers (WeTracked) and Google Ads attribution for an external
 * checkout look for them (`docs/INTEGRATIONS.md` §"WeTracked").
 *
 * ## Consent, by construction
 *
 * Google's tag sets these cookies only when the shopper allowed ad storage (the theme's consent
 * banner or Consent Mode's `ad_storage`); a refusal means no cookie and nothing to read. This
 * module reads **only** the four cookies named below, only when they exist, and it **never
 * reads the page URL** for a `gclid` itself — so the banner's decision is honoured without the
 * script having to know which consent tool the theme uses. The same rule as `_ga`
 * (`./ga-cookies.ts`, "Consent, by construction"). The tag also keeps a JSON copy in
 * `localStorage['_gcl_ls']`; it is not read — the cookies are enough and consistent with the
 * GA reader.
 *
 * ## The cookie layout
 *
 * From gtag.js itself (read 2026-09-22): the value is built as
 * `["GCL", <unix seconds>, <id>].join(".")`, with optional extra dot-separated segments
 * appended after the id. The routing is `gclid` → `_gcl_aw`, `wbraid` → `_gcl_gb`,
 * `gbraid` → `_gcl_ag`, `dclid` → `_gcl_dc`, and the tag validates every id against
 * `/^[\w-]+$/` before storing it. So a value is `GCL.<digits>.<id>[.<more>]`, the id being the
 * third dot-field, and that is exactly what {@link parseGclCookieValue} accepts: first field
 * `GCL`, second all digits, third matching {@link AD_CLICK_ID_PATTERN}. Google says to treat
 * click ids as opaque and of any length, so the only bound is the generous
 * {@link PEAK_AD_CLICK_ID_MAX_LENGTH}; nothing beyond the charset the tag itself enforces is
 * assumed. The `_gcl` prefix is configurable per tag (`cookie_prefix`); only the default is
 * read, and a merchant who changed it gets no click ids on the order.
 *
 * {@link AD_CLICK_ID_PATTERN} is the **single source**: the create endpoint validates the block
 * against it before storing (`src/lib/checkout/create/create-checkout.ts`) and the order
 * builder checks the stored column against it before writing an attribute
 * (`src/lib/shopify/orders/build-order-input.ts`).
 */

/** The click ids the storefront found. Every member is absent when its cookie is not. */
export type YcAdClickIds = {
  /** Google Ads, from `_gcl_aw`. */
  gclid?: string;
  /** Google Ads on iOS 14+, web-to-web, from `_gcl_gb`. */
  wbraid?: string;
  /** Google Ads on iOS 14.5+, app-to-web, from `_gcl_ag`. */
  gbraid?: string;
  /** Display & Video 360 / Campaign Manager, from `_gcl_dc`. */
  dclid?: string;
};

/**
 * A click id as gtag.js validates it (`/^[\w-]+$/`), bounded. Google documents no length and
 * says to treat the id as opaque; 200 is well past any real one (a `gclid` is ~90–100
 * characters, a `wbraid`/`gbraid` shorter) while still keeping a hostile cookie jar out of
 * the payload.
 */
export const PEAK_AD_CLICK_ID_MAX_LENGTH = 200;
export const AD_CLICK_ID_PATTERN = /^[\w-]{1,200}$/;

/** `_gcl_<suffix>` → payload member. Only the default `_gcl` prefix is read. */
const AD_CLICK_COOKIES: readonly (readonly [string, keyof YcAdClickIds])[] = [
  ['_gcl_aw', 'gclid'],
  ['_gcl_gb', 'wbraid'],
  ['_gcl_ag', 'gbraid'],
  ['_gcl_dc', 'dclid'],
];

const GCL_VERSION = 'GCL';
const DIGITS = /^\d+$/;

/**
 * The click id inside a `_gcl_*` cookie value, or `null`.
 *
 * `GCL.<digits>.<id>[.<more>]`: anything else — another first field, a timestamp that is not
 * digits, an id outside the tag's own charset or over the bound, fewer than three fields — is
 * not a cookie Google wrote and is dropped. Segments after the id are ignored.
 */
export function parseGclCookieValue(value: string): string | null {
  const fields = value.split('.');
  if (fields.length < 3 || fields[0] !== GCL_VERSION || !DIGITS.test(fields[1] as string)) {
    return null;
  }
  const id = fields[2] as string;
  return AD_CLICK_ID_PATTERN.test(id) ? id : null;
}

/**
 * Parse a `document.cookie` string for the four `_gcl_*` cookies.
 *
 * Pure, and the same rules as `parseGaCookies`: names matched exactly, values not decoded, and
 * the first *valid* occurrence of a name wins. `undefined` when none was found, so the payload
 * builder can leave the member out.
 */
export function parseGoogleClickCookies(cookieString: string): YcAdClickIds | undefined {
  if (typeof cookieString !== 'string' || cookieString === '') return undefined;

  let result: YcAdClickIds | undefined;
  const parts = cookieString.split(';');
  for (let i = 0; i < parts.length; i += 1) {
    const part = (parts[i] as string).trim();
    const eq = part.indexOf('=');
    if (eq <= 0) continue;
    const name = part.slice(0, eq).trim();

    for (let j = 0; j < AD_CLICK_COOKIES.length; j += 1) {
      const entry = AD_CLICK_COOKIES[j] as readonly [string, keyof YcAdClickIds];
      if (name !== entry[0]) continue;
      if (result !== undefined && result[entry[1]] !== undefined) break;
      const id = parseGclCookieValue(part.slice(eq + 1).trim());
      if (id === null) break;
      if (result === undefined) result = {};
      result[entry[1]] = id;
      break;
    }
  }
  return result;
}

/** The slice of `document` this module reads. Structural so it can be tested without a DOM. */
type CookieDocumentLike = { cookie?: unknown };

/**
 * Read the click ids off a document, never throwing — `document.cookie` can throw in a
 * sandboxed frame or be shadowed by a theme, and either is "no cookies" (see
 * `readGaCookies`). `undefined` when there is nothing to send.
 */
export function readGoogleClickCookies(
  documentLike: CookieDocumentLike | null | undefined,
): YcAdClickIds | undefined {
  try {
    if (documentLike === null || documentLike === undefined) return undefined;
    const cookie = documentLike.cookie;
    return typeof cookie === 'string' ? parseGoogleClickCookies(cookie) : undefined;
  } catch {
    return undefined;
  }
}
